import * as R from 'ramda';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { Routes } from '../../config/routes';
import { isInvalidStepError } from '../../providers/validation/stepError';
import { ValidationContext } from '../../providers/ValidationContext';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import documentCapture, { Constants } from '../../services/documentCapture';
import BackToChooser from '../validation/components/BackToChooser';
import DocumentCaptureStep from '../../providers/validation/steps/DocumentCaptureStep';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';
import { DEFAULT_SUPPORTED_DOCUMENT_TYPES, DocumentType } from '../../services/documentCapture/documentCapture';
import { useScopeRequest } from '../../providers/ScopeRequestContext';
import WindowProvider from '../../common/window';
import {useTheme} from "../../providers/theme/ThemeProvider";

enum Status {
  Preparation = 'preparation',
  OnfidoLoading = 'onfidoLoading',
  OnfidoReady = 'onfidoReady',
  OnfidoDone = 'onfidoDone',
}

export function DocumentCapture(): JSX.Element {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>(Status.Preparation);
  const onfidoContainerRef = useRef(null);
  const mutationObsRef = useRef<MutationObserver | null>(null);

  const { getSupportedDocumentTypes } = useScopeRequest();
  const { processStep, verificationStarted, getRouteForNextStep } = useContext(ValidationContext);
  const { initialized } = useContext(FlowConfigContext);
  const { t } = useTranslation();
  const { networkName } = useContext(FlowConfigContext);
  const [documentContainerClass, setDocumentContainerClass] = useState<string>('');
  const { darkModeSupported } = useTheme();

  const performDocumentCapture = async () => {
    setStatus(Status.OnfidoLoading);
    const result = await processStep(
      new DocumentCaptureStep(getSupportedDocumentTypes() || DEFAULT_SUPPORTED_DOCUMENT_TYPES),
    );
    if (result.isSuccessful() || result.failedWith(isInvalidStepError)) {
      setStatus(Status.OnfidoDone);
      await documentCapture.cleanup();
    } else {
      setStatus(Status.Preparation);
    }
  };

  useEffect(() => {
    if (status !== Status.OnfidoDone) {
      return;
    }

    getRouteForNextStep().then((route) => route && navigate(route));
  }, [status]);

  useEffect(() => {
    if (onfidoContainerRef && onfidoContainerRef.current) {
      // add a MutationObserver to observe the onfido container. When OnFido is ready, it
      // will be added to the container and the observer will be called with the change.
      const observer = new MutationObserver((mutations) => {
        // check if there is any node added to the container.
        if (R.find((mut) => mut.addedNodes && mut.addedNodes.length > 0, mutations)) {
          setStatus(Status.OnfidoReady);
        }
      });

      // start the observer attached to the onfidoContainer. It will only observe element changes on the root level of the component.
      // A reference the component will be saved so it can be 'destroyed' later.
      observer.observe(onfidoContainerRef.current, { childList: true, subtree: false });
      mutationObsRef.current = observer;
    }
    return () => {
      // stop the observer
      mutationObsRef.current?.disconnect();
    };
  }, [onfidoContainerRef]);

  useEffect(() => {
    let docClassName = 'document-container';
    if (!WindowProvider.isInIframe()) {
      docClassName += ' notIframe';
    }
    setDocumentContainerClass(docClassName);
  }, [networkName, darkModeSupported]);

  const renderPreparation = (supportedDocumentTypes: DocumentType[]) => (
    <>
      <div className="mt-6" />
      <BackToChooser currentRoute={Routes.DOCUMENT_CAPTURE} />
      <TitleContainer marginBottomClass="mb-2" data-testid="DOCUMENT_PREPARATION_TITLE">
        <h1>{t('screens.documentPass.preparation.title')}</h1>
      </TitleContainer>
      <div className="text-lg leading-6 mt-4 text-textParagraph" data-testid="DOCUMENT_PREPARATION_SUBTITLE">
        <p>{t('screens.documentPass.preparation.subtitle')}</p>
        <ul className="list-disc text-base px-10 pt-3">
          {supportedDocumentTypes.map((docType) => (
            <li key={docType}>{t(`screens.documentPass.preparation.documentTypes.${docType}`)}</li>
          ))}
        </ul>
      </div>

      <div className="full-width-ruler my-5" />

      <div className="text-sm leading-6 mb-8 text-textParagraph" data-testid="DOCUMENT_PREPARATION_DESCRIPTION">
        <span className="textDetail">
          {t('screens.documentPass.preparation.description', { context: networkName })}
        </span>
      </div>

      <ButtonContainer bottomMargin="mb-9">
        <Button
          style={ButtonStyle.DEFAULT}
          disabled={!initialized || !verificationStarted()}
          loading={status === Status.OnfidoLoading}
          onClick={performDocumentCapture}
          dataTestid="CONTINUE_BUTTON"
        >
          <span>{t('screens.documentPass.preparation.continueButton')}</span>
        </Button>
      </ButtonContainer>
    </>
  );

  return (
    <>
      <div className={documentContainerClass} ref={onfidoContainerRef} id={Constants.DocumentCaptureContainerId} />
      {status !== Status.OnfidoReady && (
        <Container verticalPadding="py-0">
          {renderPreparation(getSupportedDocumentTypes() || DEFAULT_SUPPORTED_DOCUMENT_TYPES)}
        </Container>
      )}
    </>
  );
}
export default withRouteAnalytics(DocumentCapture, Screens.DOCUMENT_CAPTURE);
